import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LivenessEnum } from '../../../api/targeting/enum/liveness.enum';
import { ContactsPublicApi } from '../../../api/targeting/contacts-public.api';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { ContactModel } from '../model/contact.model';
import { OutletModel } from '../../../Discover/Targeting/models/outlet.model';
import { PrivateDataModel } from '../../../Discover/Targeting/models/private-data.model';
import { TargetingResultModel } from '../../../Discover/Targeting/models/targeting-result.model';
import { BaseJournodexEntityModel } from '../../../Discover/Targeting/models/base-journodex-entity.model';
import { UserNoteModel } from '../../../Common/Misc/models/user-note.model';
import { JournodexEntityFactory } from '../../../Discover/Targeting/factories/journodex-entity.factory';
import { EmploymentFactory } from '../../../Discover/Targeting/factories/employment.factory';

// Same order as tabs are listed in TargetingRHPContextsComponent
export enum OutletContactTabType {
    contactProfile = 'contactProfile',
    outletProfile = 'outletProfile',
    staffSearch = 'staffSearch',
    affiliates = 'affiliates',
    listMembership = 'listMembership',
    recentCoverage = 'recentCoverage',
    socialMedia = 'socialMedia',
    interactionView = 'interactionView',
    recommendedSearch = 'recommendedSearch'
}

interface RhpEntityChange {
    entityType: EntityType;
    outlet?: OutletModel;
    contact?: ContactModel;
}


@Injectable()
export class TargetingRhpContextsService implements OnDestroy {

    // These emit once the GET calls finish
    currentEntityType: EntityType;
    private currentEntityType$ = new BehaviorSubject<EntityType>(null);

    protected contact: ContactModel;
    protected contact$: BehaviorSubject<ContactModel> = new BehaviorSubject<ContactModel>(null);

    protected outlet: OutletModel;
    protected outlet$: BehaviorSubject<RhpEntityChange> = new BehaviorSubject<RhpEntityChange>({entityType: EntityType.CONTACT, contact: null});

    // @TODO - consolidate contact and outlet BehaviorSubjects into entity
    private entity$: BehaviorSubject<OutletModel | ContactModel> = new BehaviorSubject<OutletModel | ContactModel>(null);

    suggestedOutletId: string;

    private contactPrivateData: PrivateDataModel;
    private contactPrivateData$: BehaviorSubject<PrivateDataModel> = new BehaviorSubject<PrivateDataModel>(null);

    private outletPrivateData: PrivateDataModel;
    private outletPrivateData$: BehaviorSubject<PrivateDataModel> = new BehaviorSubject<PrivateDataModel>(null);

    listMembershipIds: number[] = null;

    private closeTakeOver$: Subject<void> = new Subject();

    // This one emits immediately when set by the caller to make
    // some UI elements snappier
    // TODO : It might be better to have a 'selectedId' object that is either

    // an outletId or a contactId instead of having two.
    outletId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    contactId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    currentOutletContactTab$: BehaviorSubject<OutletContactTabType> = new BehaviorSubject<OutletContactTabType>(null);

    // Current Tab is a non-outlet-contact tab that has been set by another caller
    // When this is set, this service will not trigger auto-switching of tabs
    // Even if we go contact -> outlet or vice versa
    currentTab: number = null;
    currentTab$: BehaviorSubject<number> = new BehaviorSubject<number> (null);

    outletContactTabsVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    hideContactProfile = false;
    hideOutletProfile = false;

    contactLoading: boolean = false;
    protected contactLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.contactLoading);

    outletLoading: boolean = false;
    protected outletLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.outletLoading);


    private subscriptions: Subscription[] = [];

    constructor(
        protected contactsPublicApi: ContactsPublicApi,
        private outletsPublicApi: OutletsPublicApi,
        protected journodexEntityFactory: JournodexEntityFactory,
        private employmentFactory: EmploymentFactory
        // private discoverRoutingService: DiscoverRoutingService,
    ) {
        // This will probably be reworked and now is the time

        /* this.discoverRoutingService.getCurrentRoute().subscribe(x => {
            this.outletContactTabsVisible$.next(false);
            this.currentEntityType = null;
        });*/
    }

    setCurrentEntityType(type: EntityType): void {
        this.currentEntityType = type;
        this.currentEntityType$.next(this.currentEntityType);
    }

    getCurrentEntityType(): Observable<EntityType> {
        return this.currentEntityType$;
    }

    setContact(
        contactId: string,
        employmentIdToSelect?: number,
        tabToSwitch: OutletContactTabType = OutletContactTabType.contactProfile,
        suggestedOutletId?: string
    ) {
        if (!contactId) {
            return;
        }
        this.suggestedOutletId = suggestedOutletId;
        const switchToProfileTab = EntityType.CONTACT !== this.currentEntityType;

        this.setCurrentEntityType(EntityType.CONTACT);
        this.fetchContact(contactId, employmentIdToSelect).then(done => {
            this.outletContactTabsVisible$.next(true);
            if (switchToProfileTab && (this.currentTab === null || this.currentTab === 0)) {
                this.currentOutletContactTab$.next(tabToSwitch);
            }
        });
    }

    setOutlet(outletId: string, tabToSwitch: OutletContactTabType = OutletContactTabType.outletProfile) {
        const switchToProfileTab = EntityType.OUTLET !== this.currentEntityType;

        this.setCurrentEntityType(EntityType.OUTLET);
        this.fetchOutlet(outletId, EntityType.OUTLET).then(done => {
            this.outletContactTabsVisible$.next(true);
            if (switchToProfileTab && (this.currentTab === null || this.currentTab === 0)) {
                this.currentOutletContactTab$.next(tabToSwitch);
            }
        });
        this.fetchPrivateData(outletId, EntityType.OUTLET);
    }

    setFullProfileContact(contact: ContactModel, employmentIdToSelect?: number) {
        this.hideContactProfile = true;
        this.hideOutletProfile = false;
        this.setCurrentEntityType(EntityType.CONTACT);
        this.currentOutletContactTab$.next(OutletContactTabType.outletProfile);
        this.outletContactTabsVisible$.next(true);
        this.contact = contact;
        this.entity$.next(this.contact);
        this.changeSelectedEmployment(employmentIdToSelect);
    }

    setFullProfileContactById(contactId: string, employmentIdToSelect?: number) {
        this.hideContactProfile = true;
        this.setCurrentEntityType(EntityType.CONTACT);
        this.currentOutletContactTab$.next(OutletContactTabType.outletProfile);

        this.fetchContact(contactId, employmentIdToSelect).then(done => {
            this.outletContactTabsVisible$.next(true);
        });
        this.fetchPrivateData(contactId, EntityType.CONTACT);
    }

    setFullProfileOutlet(outlet: OutletModel) {
        this.hideOutletProfile = true;
        this.hideContactProfile = false;
        this.setCurrentEntityType(EntityType.OUTLET);
        this.currentOutletContactTab$.next(OutletContactTabType.staffSearch);
        this.outletContactTabsVisible$.next(true);
        this.outlet = outlet;
        this.outlet$.next({entityType: EntityType.OUTLET, outlet: this.outlet});
        this.entity$.next(this.outlet);
        this.outletId$.next(outlet.id);
    }

    setFullProfileOutletById(outletId: string) {
        this.hideContactProfile = true;
        this.setCurrentEntityType(EntityType.OUTLET);
        this.currentOutletContactTab$.next(OutletContactTabType.staffSearch);

        this.fetchOutlet(outletId, EntityType.OUTLET).then(done => {
            this.outletContactTabsVisible$.next(true);
        });
        this.fetchPrivateData(outletId, EntityType.OUTLET);
    }

    setContactPrivateData(privateData: PrivateDataModel) {
        this.contactPrivateData = privateData;
        this.contactPrivateData$.next(this.contactPrivateData);
    }

    setOutletPrivateData(privateData: PrivateDataModel) {
        this.outletPrivateData = privateData;
        this.outletPrivateData$.next(this.outletPrivateData);
    }

    setEntityByResult(result: TargetingResultModel) {
        if (!result) {
            this.contact = null;
            this.outlet = null;
            this.contactId$.next(null);
            this.outletId$.next(null);
            this.contact$.next(null);
            this.outlet$.next({entityType: EntityType.CONTACT, outlet: null });
            this.entity$.next(null);
            if (result !== null) {
                this.outletContactTabsVisible$.next(false);
            }
            return;
        }
        if (result.entityType === EntityType.CONTACT) {
            this.setContact(result.id, result.selectedEmployment ? result.selectedEmployment.id : null);
            if (!result.isPrivate) {
                this.fetchPrivateData(result.id, EntityType.CONTACT);
            }
        } else {
            this.setOutlet(result.id);
        }
    }

    changeSelectedEmployment(employmentId: number) {
        if (!this.contact) {
            return;
        }
        this.contact = this.contact.clone();
        this.contact.selectedEmployment = this.contact.employmentModels.find(x => x.id === employmentId) || this.employmentFactory.createEmpty();
        this.contact$.next(this.contact);
        this.outletPrivateData$.next(this.outletPrivateData);

        if (this.contact.selectedEmployment?.outlet) {
            this.outlet = this.contact.selectedEmployment.outlet;
            this.outlet$.next({entityType: EntityType.CONTACT, outlet: this.outlet });
            this.outletId$.next(this.outlet.id);
            if (this.outlet.id && this.outlet.liveness === LivenessEnum.ACTIVE) {
                this.fetchPrivateData(this.outlet.id, EntityType.OUTLET);
            } else {
                this.outletPrivateData = null;
                this.outletPrivateData$.next(this.outletPrivateData);
            }
        } else {
            this.outlet = null;
            this.outlet$.next({entityType: EntityType.CONTACT, outlet: null });
            this.outletId$.next(null);
        }
    }

    protected fetchContact(contactId: string, employmentIdToSelect?: number): Promise<void> {
        return new Promise<void> (resolve => {
            if (this.contact && contactId === this.contact.id && !this.contact.privateContactMetadata) {
                resolve();
            }

            this.contactId$.next(contactId);
            this.outletId$.next(null);
            this.contactLoading$.next(true);

            this.contactsPublicApi.getContact(contactId).subscribe({
                next: async (contact) => {
                    this.contact = await this.journodexEntityFactory.createContact(contact);
                    if (employmentIdToSelect) {
                        this.contact.selectedEmployment = this.contact.employmentModels.find(x => x.id === employmentIdToSelect);
                    }

                    if (this.suggestedOutletId) {
                        const selectedEmployment = this.contact.employmentModels.find(x => x.outlet?.id === this.suggestedOutletId);
                        if (selectedEmployment) {
                            this.contact.selectedEmployment = selectedEmployment;
                        }
                    }

                    this.contact$.next(this.contact);
                    this.entity$.next(this.contact);
                    this.contactLoading$.next(false);

                    // TODO : Confirm that the backend always returns the full outlet data as
                    // part of the employments
                    if (this.contact.selectedEmployment && this.contact.selectedEmployment.outlet) {
                        this.outlet = this.contact.selectedEmployment.outlet;
                        this.outlet$.next({entityType: EntityType.CONTACT, outlet: this.outlet });
                        if (this.outlet.id && this.outlet.liveness === LivenessEnum.ACTIVE) {
                            this.fetchPrivateData(this.outlet.id, EntityType.OUTLET);
                        }
                    } else {
                        this.outlet = null;
                        this.outlet$.next({entityType: EntityType.CONTACT, outlet: null });
                    }

                    resolve();
                },
                error: () => {
                    this.outlet$.next({entityType: EntityType.CONTACT, outlet: null });
                    this.entity$.next(null);
                    this.contactLoading$.next(false);
                    resolve();
                }
            });
        });
    }

    protected fetchOutlet(outletId: string, entityType: EntityType): Promise<void> {
        return new Promise<void> (resolve => {

            if (this.outlet && outletId === this.outlet.id) {
                resolve();
            }

            this.outletId$.next(outletId);
            this.contactId$.next(null);
            this.outletLoading$.next(true);


            this.outletsPublicApi.getOutlet(outletId).subscribe({
                next: async (outlet) => {
                    this.outlet = await this.journodexEntityFactory.createOutlet(outlet);
                    this.outlet$.next({entityType: entityType, outlet: this.outlet });
                    this.entity$.next(this.outlet);
                    this.outletLoading$.next(false);

                    resolve();
                },
                error: () => {
                    this.outlet$.next({entityType: entityType, outlet: null });
                    this.entity$.next(null);
                    this.outletLoading$.next(false);
                    resolve();
                }
            });
        });
    }

    fetchPrivateData(entityId: string, entityType: EntityType) {
        if (entityType === EntityType.CONTACT) {
            this.contactsPublicApi.getPrivateData(entityId).subscribe(privateData => {
                this.contactPrivateData = new PrivateDataModel(privateData);
                this.contactPrivateData$.next(this.contactPrivateData);
            });
        } else {
            this.outletsPublicApi.getPrivateData(entityId).subscribe(privateData => {
                this.outletPrivateData = new PrivateDataModel(privateData);
                this.outletPrivateData$.next(this.outletPrivateData);
            });
        }
    }

    setListMembershipIds(ids: number[]) {
        this.listMembershipIds = ids;
    }

    modifyNotes(notes: UserNoteModel[], entity: BaseJournodexEntityModel, privateData: PrivateDataModel): Promise<void> {
        return new Promise<void> ((resolve, reject) => {
            if (entity instanceof ContactModel) {
                if (entity.privateContactMetadata) {
                    entity.userNotes = notes;
                    this.contactsPublicApi.editContact((entity as ContactModel).toApiModel(), this.listMembershipIds).subscribe({
                        next: () => resolve(),
                        error: () => reject()
                    });
                } else {
                    privateData.notes = notes;
                    this.contactsPublicApi.setPrivateData(entity.id, privateData.toApiModel()).subscribe({
                        next: () => resolve(),
                        error: () => reject()
                    });
                }
            } else {
                privateData.notes = notes;
                this.outletsPublicApi.setPrivateData(entity.id, privateData.toApiModel()).subscribe({
                    next: () => resolve(),
                    error: () => reject()
                });
            }
        });
    }

    clearOutlet() {
        this.outlet = null;
        this.outletId$.next(null);
        this.outlet$.next({entityType: EntityType.CONTACT, outlet: this.outlet });
    }

    clearContact() {
        this.contact = null;
        this.contactId$.next(null);
        this.contact$.next(this.contact);
    }

    selectOutletContactTab(tab: OutletContactTabType) {
        this.currentOutletContactTab$.next(tab);
    }

    selectTab(index: number) {
        this.currentTab = index;
        this.currentTab$.next(index);
    }

    /** Closes takeover dialog */
    closeTakeOver(): void {
        this.closeTakeOver$.next();
    }

    getCloseTakeOverProvider(): Observable<void> {
        return this.closeTakeOver$;
    }

    getContact(): Observable<ContactModel> {
        return this.contact$;
    }

    getContactId(): Observable<string> {
        return this.contactId$;
    }

    getOutlet(): Observable<RhpEntityChange> {
        return this.outlet$;
    }

    getOutletId(): Observable<string> {
        return this.outletId$;
    }

    getContactLoading(): Observable<boolean> {
        return this.contactLoading$;
    }

    getOutletLoading(): Observable<boolean> {
        return this.outletLoading$;
    }

    getEntity(): Observable<OutletModel | ContactModel> {
        return this.entity$;
    }

    getContactPrivateData(): Observable<PrivateDataModel> {
        return this.contactPrivateData$;
    }

    getOutletPrivateData(): Observable<PrivateDataModel> {
        return this.outletPrivateData$;
    }

    getCurrentTab() {
        return this.currentTab$;
    }

    getCurrentOutletContactTab() {
        return this.currentOutletContactTab$;
    }

    getOutletContactTabsVisible() {
        return this.outletContactTabsVisible$;
    }

    retrieveOutletPrivateData(outletId): Observable<PrivateDataModel> {
        return this.outletsPublicApi.getPrivateData(outletId).pipe(
            map(privateDataApiModel => new PrivateDataModel(privateDataApiModel)),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    return of(null);
                }

                throw error;
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
